import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { FileCategory } from '@makyoapp/makyo-sdk/models/Files'
import { ARCHIVE_EXTS, AUDIO_EXTS, IMAGE_EXTS, TEXT_EXTS, VIDEO_EXTS } from '@makyoapp/makyo-common'
import { useDebounceInput } from '@/shared/makyo-hooks'
import * as Mime from '@makyoapp/makyo-icon/mimetype'
import { Loading, Modal, Switch, Tabs, WrappedPagination } from '@makyoapp/makyo-ui'

import { useGetStorage } from '@/resolvers/org/storage/query'
import { FileManagerKind, useFileManagerContext } from '@/context/FileManagerContext'
import { getFileSize } from '@/shared/utils'

export function getMimeIcon(link: string) {
  const split = link.split('.')
  const ext = split[split.length - 1]?.toLowerCase()
  let mime
  if (ext) {
    if (VIDEO_EXTS.includes(ext)) {
      mime = 'Playlist'
    } else if (AUDIO_EXTS.includes(ext)) {
      mime = 'Audio'
    } else if (IMAGE_EXTS.includes(ext)) {
      mime = 'Image'
    } else if (TEXT_EXTS.includes(ext)) {
      mime = 'Text'
    } else if (ARCHIVE_EXTS.includes(ext)) {
      mime = 'Tar'
    } else {
      switch (ext) {
        case 'swf':
          mime = 'Flash'
          break
        case 'apk':
          mime = 'Apk'
          break
        case 'doc':
        case 'docx':
        case 'odt':
          mime = 'Doc'
          break
        case 'xls':
        case 'csv':
        case 'xlsx':
        case 'ods':
          mime = 'Spreadsheet'
          break
        case 'ppt':
        case 'pptx':
        case 'odp':
          mime = 'Powerpoint'
          break
        case 'svg':
          mime = 'ImageSvg'
          break
        case 'pdf':
          mime = 'Pdf'
          break
        case 'json':
          mime = 'Json'
          break
        case 'css':
          mime = 'Css'
          break
        default:
          mime = 'Generic'
          break
      }
    }
  }
  return mime as keyof typeof Mime
}

export function MyFilesPanel() {
  const { t } = useTranslation()

  const {
    onChange,
    onHide,
    hideImage,
    setHideImage,
    maxHeight,
    gridCols,
    fileTypes: rawFileTypes,
    showHideImageToggle,
  } = useFileManagerContext()

  const [page, setPage] = useState(1)
  const [debSearch, cancelDebSearch, setDebSearch, search, setSearch] = useDebounceInput('')

  const resetSearch = useCallback(() => {
    cancelDebSearch()
    setDebSearch('')
    setSearch('')
  }, [cancelDebSearch, setDebSearch, setSearch])

  const fileTypes = useMemo(() => {
    let types: FileCategory[] = hideImage
      ? [FileCategory.Audio, FileCategory.Video, FileCategory.Document, FileCategory.Other]
      : []

    if (rawFileTypes && Array.isArray(rawFileTypes) && rawFileTypes.length > 0) {
      types = [...rawFileTypes]
    }
    return types
  }, [hideImage, rawFileTypes])

  // Access Org Storage
  const storageQuery = useGetStorage({
    params: {
      page,
      search: debSearch,
      categories: fileTypes,
    },
  })

  // derived state
  const storage = storageQuery.data || {
    items: [],
    totalCount: 0,
  }
  const isLoading = storageQuery.isLoading

  return (
    <Tabs.Panel data-panel-id={FileManagerKind.MyFiles} noPadding eventKey={FileManagerKind.MyFiles}>
      <div className='flex items-center w-full'>
        <Modal.Search
          placeholder={t('Search')}
          onChange={(e) => {
            setSearch(e || '')
          }}
          value={search}
          onClear={resetSearch}
          className='w-full'
        />

        {showHideImageToggle && (
          <label className='border-b ltr:border-l rtl:border-r bdr h-10 min-w-[150px] cursor-pointer flex-c px-4'>
            <Switch checked={hideImage} onClick={() => setHideImage(!hideImage)} />
            <Trans>Hide Images</Trans>
          </label>
        )}
      </div>
      {isLoading ? (
        <div
          className='flex-cc'
          style={{
            height: maxHeight ? maxHeight : '60vh',
          }}
        >
          <Loading fullHeight={false} />
        </div>
      ) : (
        //p-4 mk-scroll grid grid-cols-4 gap-6 max-h-[60vh] overflow-y-auto
        <div
          className={clsx('p-4 mk-scroll grid gap-6 overflow-y-auto', `grid-cols-${gridCols ?? 4}`)}
          style={{
            maxHeight: maxHeight ? maxHeight : '60vh',
          }}
        >
          {storage.items.map((file, idx) => {
            //const splitName = img._Link.split('/')
            //const name = splitName[splitName.length - 1]
            const name = file._Name
            const size = file._Size
            const mime = getMimeIcon(file._Link)
            const Icon = Mime[mime]
            const sz = getFileSize(size)

            return (
              <div
                key={idx}
                onClick={() => {
                  onChange(FileManagerKind.MyFiles, file._Link)
                  onHide?.()
                }}
              >
                <div className='mk-fm-file flex-c hover:opacity-60 relative cursor-pointer'>
                  <Icon className='mk-fm-file-icon flex-shrink-0 flex-grow-0' width={36} height={36} />
                  <div className='txt'>
                    <div className='txt font-semibold' title={name}>
                      {name}
                    </div>
                    <div className='text-light'>{t('Size: {{size}}', { size: sz })}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <div className='py-4'>
        <WrappedPagination
          gap='none'
          totalCount={storage.totalCount}
          page={page}
          perPage={20}
          onChange={(f, t, n) => {
            if (page !== n) {
              setPage(n)
            }
          }}
        />
      </div>
    </Tabs.Panel>
  )
}
